import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, SlidersHorizontal, X, ChevronLeft, ChevronRight,
  UserRound, Users, MoreVertical, KeyRound, Trash2,
  Copy, CheckCheck, AlertTriangle, CheckCircle2,
  Eye, EyeOff, Wand2,
} from 'lucide-react';
import { usersAPI } from '../../api/users';
import PageHeader from '../../components/ui/PageHeader';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const genderColor = {
  male:   { bg: 'rgba(59,130,246,0.15)',  text: '#93c5fd' },
  female: { bg: 'rgba(236,72,153,0.15)',  text: '#f9a8d4' },
  other:  { bg: 'rgba(139,92,246,0.15)',  text: '#c4b5fd' },
};

const GENDERS = ['all', 'male', 'female', 'other'];

const PER_PAGE = 15;

// ── Skeleton row ─────────────────────────────────────────────
function UserSkeleton() {
  return (
    <div className="card flex items-center gap-3">
      <div className="skeleton h-11 w-11 flex-shrink-0 rounded-full" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="skeleton h-3.5 w-32 rounded" />
        <div className="skeleton h-3 w-24 rounded" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="skeleton h-3.5 w-20 rounded" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
    </div>
  );
}

// ── Copy button ──────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
      className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold transition-all active:scale-95"
      style={{
        background: copied ? 'rgba(5,150,105,0.2)' : 'rgba(255,255,255,0.1)',
        color: copied ? '#6ee7b7' : 'rgba(255,255,255,0.7)',
        border: copied ? '1px solid rgba(5,150,105,0.3)' : '1px solid rgba(255,255,255,0.15)',
      }}
    >
      {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ── Action menu sheet ─────────────────────────────────────────
function ActionMenuSheet({ user, onClose, onResetPassword, onDelete }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-5 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-widest"
             style={{ color: 'rgba(212,160,23,0.6)' }}>
            Actions for
          </p>
          <p className="text-base font-semibold text-white mt-0.5">{user.name}</p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.mobile}</p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={onResetPassword}
            className="flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-semibold
                       text-left transition-all active:scale-[0.98]"
            style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#93c5fd' }}
          >
            <KeyRound size={16} className="flex-shrink-0" />
            Reset Password
          </button>

          <button
            onClick={onDelete}
            className="flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-semibold
                       text-left transition-all active:scale-[0.98]"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}
          >
            <Trash2 size={16} className="flex-shrink-0" />
            Delete Account
          </button>

          <button
            onClick={onClose}
            className="rounded-2xl py-3 text-sm font-semibold transition-all active:scale-[0.98]"
            style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm sheet ──────────────────────────────────────
function DeleteConfirmSheet({ user, onClose, onConfirm, submitting, error }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
               style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <AlertTriangle size={18} style={{ color: '#fca5a5' }} />
          </div>
          <div>
            <h3 className="font-serif text-base font-semibold text-white">Delete Account</h3>
            <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {user.name} · {user.mobile}
            </p>
          </div>
        </div>

        <p className="text-sm mb-5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
          This will <span style={{ color: '#fca5a5', fontWeight: 600 }}>permanently delete</span> the
          account along with all deposits, withdrawals, and related data. This action cannot be undone.
        </p>

        {error && (
          <p className="mb-4 rounded-xl border px-3 py-2.5 text-sm"
             style={{ background: 'rgba(239,68,68,0.12)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5
                       text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', boxShadow: '0 4px 16px rgba(220,38,38,0.35)' }}
          >
            {submitting ? (
              <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <><Trash2 size={15} /> Delete</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reset password sheet ──────────────────────────────────────
function ResetPasswordSheet({ user, newPassword, onClose, onConfirm, submitting, error }) {
  const [mode,      setMode]      = useState('generate'); // 'generate' | 'custom'
  const [customPwd, setCustomPwd] = useState('');
  const [showPwd,   setShowPwd]   = useState(false);

  const canSubmit = mode === 'generate' || customPwd.trim().length >= 6;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {newPassword ? (
          /* ── Success state ── */
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                   style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)' }}>
                <CheckCircle2 size={18} style={{ color: '#6ee7b7' }} />
              </div>
              <div>
                <h3 className="font-serif text-base font-semibold text-white">Password Reset</h3>
                <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.name}</p>
              </div>
            </div>

            <p className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
              New password — share this with the user:
            </p>

            <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 mb-5"
                 style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)' }}>
              <p className="font-mono text-lg font-bold tracking-widest text-white">{newPassword}</p>
              <CopyBtn text={newPassword} />
            </div>

            <button
              onClick={onClose}
              className="w-full rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Done
            </button>
          </>
        ) : (
          /* ── Form state ── */
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                   style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)' }}>
                <KeyRound size={18} style={{ color: '#93c5fd' }} />
              </div>
              <div>
                <h3 className="font-serif text-base font-semibold text-white">Reset Password</h3>
                <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {user.name} · {user.mobile}
                </p>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-1 rounded-2xl p-1 mb-5"
                 style={{ background: 'rgba(255,255,255,0.07)' }}>
              {[
                { key: 'generate', label: 'Auto Generate', icon: Wand2 },
                { key: 'custom',   label: 'Custom',        icon: KeyRound },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setMode(key)}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5
                             text-xs font-semibold transition-all"
                  style={mode === key
                    ? { background: 'linear-gradient(135deg,#1d4ed8,#1e40af)', color: '#fff', boxShadow: '0 2px 8px rgba(29,78,216,0.35)' }
                    : { color: 'rgba(255,255,255,0.45)' }
                  }
                >
                  <Icon size={12} />
                  {label}
                </button>
              ))}
            </div>

            {/* Custom password input */}
            {mode === 'custom' && (
              <div className="mb-5">
                <label className="input-label mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={customPwd}
                    onChange={(e) => setCustomPwd(e.target.value)}
                    disabled={submitting}
                    className="input-field w-full pr-10 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {customPwd.length > 0 && customPwd.length < 6 && (
                  <p className="mt-1.5 text-[11px]" style={{ color: '#fca5a5' }}>
                    Password must be at least 6 characters.
                  </p>
                )}
              </div>
            )}

            {mode === 'generate' && (
              <p className="text-sm mb-5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                A random 10-character password will be generated. Make sure to share it with the user.
              </p>
            )}

            {error && (
              <p className="mb-4 rounded-xl border px-3 py-2.5 text-sm"
                 style={{ background: 'rgba(239,68,68,0.12)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}>
                {error}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(mode === 'custom' ? customPwd.trim() : undefined)}
                disabled={submitting || !canSubmit}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5
                           text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#1d4ed8,#1e40af)', color: '#fff', boxShadow: '0 4px 16px rgba(29,78,216,0.35)' }}
              >
                {submitting ? (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : mode === 'generate' ? (
                  <><Wand2 size={15} /> Generate</>
                ) : (
                  <><KeyRound size={15} /> Set Password</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── User card ────────────────────────────────────────────────
function UserCard({ user, onClick, onAction }) {
  const initials = user.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const gc = genderColor[user.gender] || genderColor.other;

  return (
    <div
      className="card w-full flex items-center gap-3 transition-all duration-200 hover:border-gold/40"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      {/* Avatar */}
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
           style={{ background: 'linear-gradient(135deg, #1448c4 0%, #0a2260 100%)' }}>
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-semibold text-white truncate">{user.name}</p>
          <span className="flex-shrink-0 rounded-lg px-1.5 py-0.5 text-[10px] font-bold"
                style={{ background: 'rgba(212,160,23,0.15)', color: '#d4a017' }}>
            #{user.id}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {user.mobile}
          </p>
          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ background: gc.bg, color: gc.text }}>
            {user.gender}
          </span>
          {user.gold_preference_grams && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: 'rgba(212,160,23,0.12)', color: '#f0c040', border: '1px solid rgba(212,160,23,0.25)' }}>
              🥇 {user.gold_preference_grams}g
            </span>
          )}
        </div>
      </div>

      {/* Balance + action */}
      <div className="flex flex-shrink-0 items-center gap-2">
        <div className="text-right">
          <p className="text-sm font-semibold text-gold-shimmer">
            {formatINR(user.total_balance)}
          </p>
          <p className="mt-0.5 text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {formatDate(user.registered_at)}
          </p>
        </div>

        {/* ⋮ button */}
        <button
          onClick={(e) => { e.stopPropagation(); onAction(); }}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl transition-all active:scale-90"
          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <MoreVertical size={15} style={{ color: 'rgba(255,255,255,0.5)' }} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function UsersPage() {
  const navigate = useNavigate();

  // filters
  const [search, setSearch]       = useState('');
  const [gender, setGender]       = useState('all');
  const [from,   setFrom]         = useState('');
  const [to,     setTo]           = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // data
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // action sheets
  const [actionUser,   setActionUser]   = useState(null);  // user object
  const [sheet,        setSheet]        = useState(null);  // 'menu' | 'delete' | 'reset'
  const [submitting,   setSubmitting]   = useState(false);
  const [actionError,  setActionError]  = useState('');
  const [newPassword,  setNewPassword]  = useState('');

  const closeSheet = () => {
    if (submitting) return;
    setSheet(null);
    setActionUser(null);
    setActionError('');
    setNewPassword('');
  };

  const openMenu = (user) => {
    setActionUser(user);
    setActionError('');
    setNewPassword('');
    setSheet('menu');
  };

  // ── Delete ──────────────────────────────────────────────
  const handleDelete = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      await usersAPI.deleteUser(actionUser.id);
      setUsers((prev) => prev.filter((u) => u.id !== actionUser.id));
      closeSheet();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to delete user.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset password ───────────────────────────────────────
  const handleResetPassword = async (customPassword) => {
    setSubmitting(true);
    setActionError('');
    try {
      const payload = customPassword ? { password: customPassword } : {};
      const { data } = await usersAPI.resetPassword(actionUser.id, payload);
      setNewPassword(data.data.new_password);
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  // debounce search
  const searchDebounce = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(searchDebounce.current);
  }, [search]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [gender, from, to]);

  // ── Fetch ────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: PER_PAGE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (gender !== 'all') params.gender = gender;
      if (from)  params.from = from;
      if (to)    params.to   = to;

      const { data } = await usersAPI.getList(params);
      if (data.status) {
        setUsers(data.data.users);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, gender, from, to, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Clear filters ────────────────────────────────────────
  const clearFilters = () => {
    setGender('all');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const hasActiveFilters = gender !== 'all' || from || to;

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Users"
        right={
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="relative flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90"
            style={{
              background: showFilters
                ? 'rgba(212,160,23,0.2)'
                : 'rgba(255,255,255,0.08)',
              border: showFilters
                ? '1px solid rgba(212,160,23,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <SlidersHorizontal size={15} style={{ color: showFilters ? '#d4a017' : 'rgba(255,255,255,0.6)' }} />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                    style={{ background: '#d4a017' }} />
            )}
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.35)' }} />
          <input
            type="text"
            placeholder="Search by name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-9"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="card flex flex-col gap-4 animate-fade-in">
            {/* Gender tabs */}
            <div>
              <p className="input-label mb-2">Gender</p>
              <div className="flex gap-2 flex-wrap">
                {GENDERS.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGender(g)}
                    className="rounded-xl px-3 py-1.5 text-xs font-semibold capitalize transition-all duration-150"
                    style={{
                      background: gender === g
                        ? 'linear-gradient(135deg,#d4a017,#f0c040)'
                        : 'rgba(255,255,255,0.07)',
                      color: gender === g ? '#fff' : 'rgba(255,255,255,0.55)',
                      border: gender === g
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {g === 'all' ? 'All' : g}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="input-label">From</label>
                <input type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)}
                       className="input-field" />
              </div>
              <div className="flex flex-col">
                <label className="input-label">To</label>
                <input type="date" value={to}
                       onChange={(e) => setTo(e.target.value)}
                       className="input-field" />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters}
                      className="flex items-center gap-1.5 self-start text-xs font-medium transition-opacity hover:opacity-70"
                      style={{ color: '#fca5a5' }}>
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Summary row ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing {users.length} of {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </p>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="card text-sm text-center py-6" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading skeletons ── */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 6 }).map((_, i) => <UserSkeleton key={i} />)}
          </div>
        )}

        {/* ── User list ── */}
        {!loading && !error && users.length > 0 && (
          <div className="flex flex-col gap-3">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onClick={() => navigate(`/users/${user.id}`)}
                onAction={() => openMenu(user)}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && users.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <Users size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {debouncedSearch || hasActiveFilters ? 'No users match your filters' : 'No users found'}
            </p>
            {(debouncedSearch || hasActiveFilters) && (
              <button onClick={() => { setSearch(''); clearFilters(); }}
                      className="text-xs font-medium"
                      style={{ color: '#d4a017' }}>
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>

            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>

      {/* ── Action sheets ── */}
      {sheet === 'menu' && actionUser && (
        <ActionMenuSheet
          user={actionUser}
          onClose={closeSheet}
          onResetPassword={() => setSheet('reset')}
          onDelete={() => setSheet('delete')}
        />
      )}

      {sheet === 'delete' && actionUser && (
        <DeleteConfirmSheet
          user={actionUser}
          onClose={closeSheet}
          onConfirm={handleDelete}
          submitting={submitting}
          error={actionError}
        />
      )}

      {sheet === 'reset' && actionUser && (
        <ResetPasswordSheet
          user={actionUser}
          newPassword={newPassword}
          onClose={closeSheet}
          onConfirm={handleResetPassword}
          submitting={submitting}
          error={actionError}
        />
      )}
    </div>
  );
}
